/**
 * Initiate Cognito login (phase 1): generate PKCE/state/nonce, persist them to
 * sessionStorage, build the /oauth2/authorize URL, and redirect to the Hosted UI.
 */

import { getCognitoConfig } from './config';
import { STORAGE_KEYS } from './constants';
import {
  createCodeVerifier,
  createCodeChallenge,
  createState,
  createNonce,
} from './pkce';

/** Build the Cognito authorize URL and persist PKCE / state / nonce to sessionStorage. */
export const buildAuthorizeUrl = async () => {
  const cfg = getCognitoConfig();

  // Fail fast if required params are missing to avoid Cognito's dead-end error page.
  const REQUIRED = ['domain', 'clientId', 'redirectUri', 'scope'];
  const missing = REQUIRED.filter((key) => !cfg[key]);
  if (missing.length) {
    throw new Error(`Cognito authorize config incomplete, missing: ${missing.join(', ')}`);
  }

  const codeVerifier = createCodeVerifier();
  const codeChallenge = await createCodeChallenge(codeVerifier);
  const state = createState();
  const nonce = createNonce();

  // Persist for the callback route.
  sessionStorage.setItem(STORAGE_KEYS.CODE_VERIFIER, codeVerifier);
  sessionStorage.setItem(STORAGE_KEYS.STATE, state);
  sessionStorage.setItem(STORAGE_KEYS.NONCE, nonce);

  const params = new URLSearchParams({
    response_type: 'code',
    client_id: cfg.clientId,
    redirect_uri: cfg.redirectUri,
    scope: cfg.scope,
    state,
    nonce,
    code_challenge: codeChallenge,
    code_challenge_method: 'S256',
  });

  return `${cfg.domain}/oauth2/authorize?${params.toString()}`;
};

/** Build the authorize URL and redirect to the Cognito Hosted UI. */
export const initiateCognitoLogin = async () => {
  try {
    const url = await buildAuthorizeUrl();
    window.location.assign(url);
  } catch (err) {
    // Avoid sending a malformed request to the Hosted UI; surface the error in-app.
    // eslint-disable-next-line no-console
    console.error('[cognito] unable to build authorize URL:', err);
    sessionStorage.setItem(
      'loginError',
      'Unable to start login. Please try again.'
    );
    window.location.assign('/loginchoice');
  }
};

export default initiateCognitoLogin;
